/**
 * No job left open — the office's "Left open" list (mockup section 5:
 * https://claude.ai/artifact/N2GjpWasJBsSGai9Xb3B2Q).
 *
 * Replaces the Missed jobs list on the Grid for anyone with the switch on.
 * Two buckets so whose turn it is reads at a glance. Every card: a face (who
 * moves next), the job, device dots, a reason, how old, and ONE button.
 *
 * Clean start (approved mockup A2, Corbin 10/10): jobs that were already open
 * when a tech was switched on land here tagged "From before", never on his
 * iPad. The office answers each one right on the card with the same three
 * answers the techs use — Done (open the job to close it out), Coming back,
 * Can't finish — through the same server answer.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, CalendarClock, Check, Inbox } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api, ApiError } from "@/lib/api";
import { type DeviceDot, type LeaveAnswerBody, type LeftOpenDevice, type LeftOpenJob } from "@/lib/left-open";
import { LeftOpenPrompt } from "./left-open-prompt";
import { cn } from "@/lib/utils";
import { useTeach } from "@/teach/teach-state";
import { openTaskFlyout } from "@/lib/task-flyout";

export type LeftOpenTurn = "office" | "tech";
export type LeftOpenAction =
    | { kind: "call_tech"; phone: string | null }
    | { kind: "open_task"; taskId: string }
    | { kind: "open_job" }
    | { kind: "nudge" }
    | { kind: "answer" };

export interface LeftOpenItem {
    jobId: string;
    displayNumber: string | null;
    title: string;
    turn: LeftOpenTurn;
    reason: string;
    reasonLabel: string;
    face: { name: string; initials: string };
    techName: string | null;
    visitAt: string | null;
    ageDays: number;
    bookedStart: string | null;
    dots: DeviceDot[];
    action: LeftOpenAction;
    /** Open before the tech's start time (clean start). Older servers omit it. */
    fromBefore?: boolean;
    /** From before and answered by the office: the answer in words. */
    answered?: string | null;
    devices?: LeftOpenDevice[];
    laterBookedStart?: string | null;
}

export interface LeftOpenListResponse {
    switchOn: boolean;
    office: LeftOpenItem[];
    tech: LeftOpenItem[];
    counts: { office: number; tech: number; total: number };
}

export const LEFT_OPEN_LIST_KEY = ["dispatch", "left-open"] as const;

/** The office read. Off (or failed) → the Grid keeps today's Missed jobs. */
export function useLeftOpenList(enabled = true) {
    return useQuery<LeftOpenListResponse>({
        queryKey: LEFT_OPEN_LIST_KEY,
        queryFn: () => api.get<LeftOpenListResponse>("/api/dispatch/left-open"),
        enabled,
        staleTime: 30_000,
        refetchInterval: 60_000,
        retry: 1,
    });
}

const FACE_COLORS = ["#3d6fb6", "#8a4fa0", "#2f7d5b", "#a0522d", "#5b6875", "#7a5c0a", "#1f6f8b", "#9b2c48"];
function faceColor(name: string): string {
    let h = 0;
    for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return FACE_COLORS[h % FACE_COLORS.length]!;
}

const MINI_DOT: Record<DeviceDot, string> = {
    pass: "bg-emerald-700",
    fail: "bg-red-700",
    todo: "border-2 border-dashed border-slate-400 dark:border-slate-500",
    gone: "bg-violet-700",
    cant: "bg-amber-700",
};

function day(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-US", { weekday: "short", timeZone: "America/Chicago" });
}

/** The office's answer screens take the same job shape the iPad uses. */
export function asPromptJob(item: LeftOpenItem): LeftOpenJob {
    return {
        jobId: item.jobId,
        displayNumber: item.displayNumber,
        customerName: null,
        siteName: item.title,
        siteLat: null,
        siteLng: null,
        status: "in_progress",
        arrivedAt: item.visitAt,
        state: "ask",
        dots: item.dots,
        devices: item.devices ?? [],
        laterBookedStart: item.laterBookedStart ?? null,
    };
}

/** "3 weeks", "12 days", "yesterday", "today" — how long it has been open. */
export function openFor(ageDays: number): string {
    if (ageDays <= 0) return "today";
    if (ageDays === 1) return "yesterday";
    if (ageDays >= 14) return `${Math.floor(ageDays / 7)} weeks`;
    return `${ageDays} days`;
}

function AnswerButtons({ item }: { item: LeftOpenItem }) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [screen, setScreen] = useState<"coming_back" | "cant_finish" | null>(null);
    const answer = useMutation({
        mutationFn: (body: Omit<LeaveAnswerBody, "tappedAt">) =>
            api.post<{ next: "done" | "review_submit" }>(`/api/jobs/${item.jobId}/leave-answer`, {
                ...body,
                tappedAt: new Date().toISOString(),
            }),
        onSuccess: (res, body) => {
            setScreen(null);
            void qc.invalidateQueries({ queryKey: LEFT_OPEN_LIST_KEY });
            if (res.next === "review_submit") {
                toast.success("Saved. Open the job to file what was tested.");
                return;
            }
            toast.success(
                body.choice === "cant_finish" ? "Saved. It went to office review." : "Saved. It went to scheduling.",
            );
        },
        onError: (err: unknown) => {
            const body = err instanceof ApiError ? (err.body as { error?: string; message?: string }) : undefined;
            if (body?.error === "PARTS_DISPOSITION_REQUIRED") {
                toast.error("Parts are still out on this job. Open the job to answer for them first.");
                return;
            }
            toast.error(body?.message ?? "Couldn't save that. Try again.");
        },
    });
    const small = "inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-3 text-sm font-bold text-white";
    return (
        <>
            <div className="flex flex-wrap gap-2" data-testid={`left-open-answers-${item.jobId}`}>
                <button
                    type="button"
                    className={cn(small, "bg-emerald-700 hover:bg-emerald-800")}
                    onClick={() => navigate(`/jobs/${item.jobId}`)}
                    title="Open the job to close it out"
                >
                    <Check className="h-4 w-4" aria-hidden /> Done
                </button>
                <button
                    type="button"
                    className={cn(small, "bg-blue-700 hover:bg-blue-800")}
                    onClick={() => setScreen("coming_back")}
                    data-testid={`left-open-office-coming-back-${item.jobId}`}
                >
                    <CalendarClock className="h-4 w-4" aria-hidden /> Coming back
                </button>
                <button
                    type="button"
                    className={cn(small, "bg-red-700 hover:bg-red-800")}
                    onClick={() => setScreen("cant_finish")}
                    data-testid={`left-open-office-cant-finish-${item.jobId}`}
                >
                    <Ban className="h-4 w-4" aria-hidden /> Can&apos;t finish
                </button>
            </div>
            {screen && (
                <LeftOpenPrompt
                    key={screen}
                    job={asPromptJob(item)}
                    trigger="office_request"
                    initialScreen={screen}
                    onSite={null}
                    canStayHere={false}
                    busy={answer.isPending}
                    onStillHere={() => setScreen(null)}
                    onAllDone={() => navigate(`/jobs/${item.jobId}`)}
                    onAnswer={(body) => answer.mutate(body)}
                    officeMode
                    onCancel={() => setScreen(null)}
                />
            )}
        </>
    );
}

function Card({ item }: { item: LeftOpenItem }) {
    const nudge = useMutation({
        mutationFn: () => api.post(`/api/jobs/${item.jobId}/left-open/nudge`),
        onSuccess: () => toast.success(`Nudged ${item.techName ?? "the tech"}.`),
        onError: () => toast.error("Couldn't send the nudge. Try again."),
    });
    const tone = item.turn === "office" ? "stop" : "go";
    const visitDay = day(item.visitAt);
    const bookedDay = day(item.bookedStart);
    const reasonText = item.reason === "booked" && bookedDay ? `${bookedDay} booked` : item.reasonLabel;
    const btn = cn(
        "shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-bold text-white",
        tone === "stop" ? "bg-amber-700 hover:bg-amber-800" : "bg-blue-700 hover:bg-blue-800",
    );
    let action: React.ReactNode;
    switch (item.action.kind) {
        case "call_tech":
            action = item.action.phone ? (
                <a className={btn} href={`tel:${item.action.phone}`}>
                    Call tech
                </a>
            ) : (
                <Link className={btn} to={`/jobs/${item.jobId}`}>
                    Open job
                </Link>
            );
            break;
        case "open_task":
            action = (
                <button
                    type="button"
                    className={btn}
                    onClick={() => item.action.kind === "open_task" && openTaskFlyout(item.action.taskId)}
                >
                    Open task
                </button>
            );
            break;
        case "open_job":
            action = (
                <Link className={btn} to={`/jobs/${item.jobId}`}>
                    Open job
                </Link>
            );
            break;
        case "nudge":
            action = (
                <button type="button" className={btn} onClick={() => nudge.mutate()} disabled={nudge.isPending}>
                    Nudge
                </button>
            );
            break;
        case "answer":
            action = <AnswerButtons item={item} />;
            break;
    }
    return (
        <div
            className={cn(
                "grid grid-cols-[40px_1fr] items-center gap-3 rounded-xl border border-border bg-background p-3",
                // The three answers get their own row so the job name keeps its room.
                item.action.kind !== "answer" && "sm:grid-cols-[40px_1fr_auto]",
            )}
            data-testid={`left-open-card-${item.jobId}`}
        >
            <span
                className="grid h-10 w-10 place-items-center rounded-full text-sm font-bold text-white"
                style={{ background: faceColor(item.face.name) }}
                title={item.face.name}
                aria-label={`Next move: ${item.face.name}`}
            >
                {item.face.initials}
            </span>
            <div className="min-w-0">
                <Link
                    to={`/jobs/${item.jobId}`}
                    className="block truncate text-[15px] font-bold leading-tight hover:underline"
                >
                    {item.title}
                    {item.displayNumber && (
                        <span className="font-mono font-medium text-muted-foreground"> · #{item.displayNumber}</span>
                    )}
                </Link>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {item.dots.length > 0 && (
                        <span className="inline-flex gap-[3px]" aria-label={`${item.dots.length} devices`}>
                            {item.dots.map((d, i) => (
                                <i key={i} className={cn("inline-block h-[11px] w-[11px] rounded-full", MINI_DOT[d])} />
                            ))}
                        </span>
                    )}
                    <span
                        className={cn(
                            "rounded-md px-2 py-0.5 text-xs font-bold",
                            item.reason === "no_answer" || item.reason === "never_closed"
                                ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200"
                                : tone === "stop"
                                  ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                                  : "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-200",
                        )}
                        data-testid={item.fromBefore ? "left-open-from-before" : undefined}
                    >
                        {reasonText}
                    </span>
                    {item.answered && (
                        <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
                            Answered: {item.answered}
                        </span>
                    )}
                    {(item.techName || visitDay) && (
                        <span>
                            {[item.techName?.split(" ")[0], item.ageDays === 0 ? "today" : visitDay]
                                .filter(Boolean)
                                .join(" · ")}
                        </span>
                    )}
                    {item.turn === "office" && item.ageDays > 0 && (
                        <span className="font-bold text-red-700 dark:text-red-300">
                            {item.fromBefore
                                ? `open ${openFor(item.ageDays)}`
                                : `${item.ageDays} day${item.ageDays === 1 ? "" : "s"}`}
                        </span>
                    )}
                </div>
            </div>
            <div className={cn("col-start-2", item.action.kind !== "answer" && "sm:col-start-auto")}>{action}</div>
        </div>
    );
}

export function LeftOpenList({ data }: { data: LeftOpenListResponse }) {
    // Teach cards (#1838): opening the list is using it.
    const { markUsed } = useTeach();
    useEffect(() => markUsed("left-open.office"), [markUsed]);
    const asOf = new Date().toLocaleString("en-US", {
        weekday: "short",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/Chicago",
    });
    return (
        <section
            className="overflow-hidden rounded-xl border border-border bg-card"
            data-testid="left-open-list"
            id="missed-jobs-section"
        >
            <header className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
                <h2 className="text-base font-bold">Left open</h2>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                    {data.counts.total}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">{asOf}</span>
            </header>
            <FromBeforeTip show={data.office.some((i) => i.fromBefore && i.action.kind === "answer")} />
            {data.counts.total === 0 ? (
                <p className="px-4 py-4 text-sm text-muted-foreground">Nothing left open. Every visit has an answer.</p>
            ) : (
                <div className="grid md:grid-cols-2">
                    <Bucket
                        title="Office's turn"
                        glyph="!"
                        tone="stop"
                        items={data.office}
                        empty="Nothing waiting on the office."
                    />
                    <Bucket
                        title="Tech's turn"
                        glyph="↻"
                        tone="go"
                        items={data.tech}
                        empty="Nothing waiting on a tech."
                        className="border-t border-border md:border-l md:border-t-0"
                    />
                </div>
            )}
        </section>
    );
}

function Bucket(props: {
    title: string;
    glyph: string;
    tone: "stop" | "go";
    items: LeftOpenItem[];
    empty: string;
    className?: string;
}) {
    return (
        <div className={cn("flex min-w-0 flex-col gap-2.5 p-3", props.className)}>
            <div className="flex items-center gap-2 text-sm font-bold">
                <span
                    className={cn(
                        "grid h-[22px] w-[22px] place-items-center rounded-full text-[11px] font-bold text-white",
                        props.tone === "stop" ? "bg-amber-700" : "bg-blue-700",
                    )}
                    aria-hidden
                >
                    {props.glyph}
                </span>
                {props.title}
                <span className="text-muted-foreground">({props.items.length})</span>
            </div>
            {props.items.length === 0 ? (
                <p className="text-sm text-muted-foreground">{props.empty}</p>
            ) : (
                props.items.map((i) => <Card key={i.jobId} item={i} />)
            )}
        </div>
    );
}

const FROM_BEFORE_TIP_KEY = "leftOpen.fromBeforeTip.v1";

/** One teaching line the first time From before jobs show up, then gone (mockup A2). */
function FromBeforeTip({ show }: { show: boolean }) {
    const [dismissed, setDismissed] = useState(() => {
        try {
            return localStorage.getItem(FROM_BEFORE_TIP_KEY) === "1";
        } catch {
            return false;
        }
    });
    if (!show || dismissed) return null;
    return (
        <div
            className="m-3 mb-0 flex flex-wrap items-center gap-3 rounded-xl bg-blue-50 p-3 text-blue-950 dark:bg-blue-950/60 dark:text-blue-100"
            data-testid="left-open-from-before-tip"
        >
            <Inbox className="h-7 w-7 shrink-0 text-blue-700 dark:text-blue-300" aria-hidden />
            <p className="min-w-0 flex-1 text-sm">
                <b>New:</b> jobs that were open before the switch came here. Pick one answer for each.
            </p>
            <button
                type="button"
                className="min-h-[40px] rounded-lg border-2 border-blue-300 px-3 text-sm font-bold hover:bg-blue-100 dark:border-blue-700 dark:hover:bg-blue-900"
                onClick={() => {
                    try {
                        localStorage.setItem(FROM_BEFORE_TIP_KEY, "1");
                    } catch {
                        /* private mode — it just shows again next time */
                    }
                    setDismissed(true);
                }}
            >
                Got it
            </button>
        </div>
    );
}
